import { DeepPartial, TableStyle } from "./tableStyle.types"

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

/** Deep copy of plain JSON data (styles hold no arrays today; copied anyway). */
const cloneValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(cloneValue)
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, cloneValue(item)])
    )
  }
  return value
}

/** A kind's `NumberFormatSpec` (`numberFormats.<kind>`) is replaced whole, never merged. */
const isAtomic = (path: string[]): boolean =>
  path.length === 2 && path[0] === "numberFormats"

/** Layer wins; nested objects merge key by key; undefined never overrides. */
const mergeValue = (
  base: unknown,
  layer: unknown,
  path: string[] = []
): unknown => {
  if (layer === undefined) return cloneValue(base)
  if (isAtomic(path) || !isPlainObject(layer) || !isPlainObject(base)) {
    return cloneValue(layer)
  }
  const merged: Record<string, unknown> = {}
  const keys = new Set([...Object.keys(base), ...Object.keys(layer)])
  keys.forEach((key) => {
    const value = mergeValue(base[key], layer[key], [...path, key])
    if (value !== undefined) merged[key] = value
  })
  return merged
}

/**
 * Layers partial styles over a base, left to right (e.g. preset, project,
 * report, tab). Returns a new object; neither the base nor the layers are
 * modified. `undefined` layers and values are skipped. Number formats are
 * atomic: a layer's `numberFormats.<kind>` replaces that kind's spec whole.
 */
export const mergeTableStyle = (
  base: TableStyle,
  ...layers: (DeepPartial<TableStyle> | undefined)[]
): TableStyle => {
  const merged = layers.reduce<unknown>(
    (acc, layer) => mergeValue(acc, layer),
    cloneValue(base)
  ) as TableStyle
  return { ...merged, version: 1 }
}
